import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Typography,
} from "@mui/material";
import apiService from "../../../ApiService";
import { API_ENDPOINTS } from "../../../ApiService";

const MuscleGroupCards = ({ selectedMuscleIds, setSelectedMuscleIds }) => {
  const [muscleGroups, setMuscleGroups] = useState([]);

  useEffect(() => {
    const fetchMuscleGroups = async () => {
      try {
        const response = await apiService.get(API_ENDPOINTS.FITNESS_GET_MUSCLE);
        setMuscleGroups(response.data);
      } catch (error) {
        console.error("Error fetching muscle groups:", error);
      }
    };
    fetchMuscleGroups();
  }, []);

  // Group muscle groups by bodyPart
  const upperBody = muscleGroups.filter((m) => m.bodyPart === "Upper Body");
  const lowerBody = muscleGroups.filter((m) => m.bodyPart === "Lower Body");
  const core = muscleGroups.filter((m) => m.bodyPart === "Core");

  // Handle individual checkbox toggle
  const handleChildChange = (event, id) => {
    if (event.target.checked) {
      setSelectedMuscleIds((prev) => [...prev, id]);
    } else {
      setSelectedMuscleIds((prev) =>
        prev.filter((selectedId) => selectedId !== id)
      );
    }
  };

  // Handle parent checkbox toggle for a given group
  const handleParentChange = (group, event) => {
    const muscleIds = group.map((m) => m.id);
    if (event.target.checked) {
      // Add any IDs that aren't already selected.
      setSelectedMuscleIds((prev) => [
        ...prev,
        ...muscleIds.filter((id) => !prev.includes(id)),
      ]);
    } else {
      // Remove all IDs for this group.
      setSelectedMuscleIds((prev) =>
        prev.filter((id) => !muscleIds.includes(id))
      );
    }
  };

  // Determine if all or some children are selected for a group.
  const isAllSelected = (group) =>
    group.length > 0 && group.every((m) => selectedMuscleIds.includes(m.id));
  const isSomeSelected = (group) =>
    group.some((m) => selectedMuscleIds.includes(m.id)) &&
    !isAllSelected(group);

  // Render a card for a given bodyPart group.
  const renderCard = (title, group) => (
    <Card sx={{ marginBottom: 2 }}>
      <CardHeader
        title={<Typography variant="h6">{title}</Typography>}
        action={
          <FormControlLabel
            label="Select All"
            control={
              <Checkbox
                checked={isAllSelected(group)}
                indeterminate={isSomeSelected(group)}
                onChange={(e) => handleParentChange(group, e)}
              />
            }
          />
        }
      />
      <CardContent>
        <FormGroup>
          {group.map((muscle) => (
            <FormControlLabel
              key={muscle.id}
              control={
                <Checkbox
                  checked={selectedMuscleIds.includes(muscle.id)}
                  onChange={(e) => handleChildChange(e, muscle.id)}
                />
              }
              label={`${muscle.region} - ${muscle.name}`}
            />
          ))}
        </FormGroup>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Select Muscle Groups
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          {renderCard("Upper Body", upperBody)}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderCard("Lower Body", lowerBody)}
        </Grid>
        <Grid item xs={12} md={12}>
          {renderCard("Core", core)}
        </Grid>
      </Grid>
    </Box>
  );
};

export default MuscleGroupCards;
